import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { Button } from "../../../components/ui/button";
import { Input } from "../../../components/ui/input";
import { Label } from "../../../components/ui/label";
import {
  Select,
  SelectItem,
  SelectTrigger,
  SelectValue,
  SelectContent,
  SelectLabel,
} from "../../../components/ui/select";
import { SearchableSelect } from "../../../components/ui/searchable-select";
import { getStateLabel } from "../../../utils/state";
import { useSelector } from "react-redux";
import { API_BASE_URL } from "../../../App";
import {
  AMC,
  AMCFormData,
  VISIT_PERIOD_OPTIONS,
  STATE_OPTIONS,
} from "../types/amc";
import {
  Refilling,
  RefillingFormData,
  QUANTITY_FIELDS,
  TEXT_FIELDS,
} from "../types/refilling";
import {
  Plus,
  Search,
  Trash2,
  Edit2,
  Calendar,
  MapPin,
  AlertCircle,
  X,
  Loader2,
  Upload,
  Image as ImageIcon,
} from "lucide-react";

export const AMCPage = () => {
  const [amcRecords, setAMCRecords] = useState<AMC[]>([]);
  const [allAMCRecords, setAllAMCRecords] = useState<AMC[]>([]);
  const [totalAMC, setTotalAMC] = useState<number>(0);
  const [page, setPage] = useState<number>(1);
  const [isCreating, setIsCreating] = useState<boolean>(false);
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [errors, setErrors] = useState<{ [key: string]: string }>({});
  const [loading, setLoading] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    visitId: string;
    reportNo: string;
  } | null>(null);
  const [editingVisitId, setEditingVisitId] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState("");
  const [uploadingImage, setUploadingImage] = useState(false);
  const [previewImage, setPreviewImage] = useState<string | null>(null);
  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalUrl, setImageModalUrl] = useState<string | null>(null);
  const [sites, setSites] = useState<any[]>([]);
  const [loadingSites, setLoadingSites] = useState(false);
  const [locationCodeSelectOpen, setLocationCodeSelectOpen] = useState(false);
  const [oldLocationCodeSelectOpen, setOldLocationCodeSelectOpen] = useState(false);
  const [usedVisitPeriods, setUsedVisitPeriods] = useState<string[]>([]);
  const [needsRefilling, setNeedsRefilling] = useState<boolean | null>(null);
  const [showRefillingForm, setShowRefillingForm] = useState(false);
  const [refillingCreatedByName, setRefillingCreatedByName] = useState<string>("");
  const [refillingCreatedById, setRefillingCreatedById] = useState<string>("");
  const [refillingFormData, setRefillingFormData] = useState<RefillingFormData>({
    state: "",
    locationCode: "",
    oldLocationCode: "",
    visitPeriod: "",
    visitDate: "",
    dcNo: "",
    abc_2_0_kg: 0,
    abc_4_0_kg: 0,
    abc_6_0_kg: 0,
    abc_9_0_kg: 0,
    co2_2_0_kg: 0,
    co2_4_5_kg: 0,
    co2_9_0_kg: 0,
    foam_9_0_lrs: 0,
    clean_agent_4_0_kg: 0,
    clean_agent_10_0_kg: 0,
    safety_pin: 0,
    pressure_gauge: 0,
    abc_hose: 0,
    co2_hose: 0,
    co2_valve: 0,
    abc_valve: 0,
    additional_field_1: "",
    additional_field_2: "",
    refillingDoc: "",
  });
  const [refillingErrors, setRefillingErrors] = useState<{ [key: string]: string }>({});
  const [refillingUploadingImage, setRefillingUploadingImage] = useState(false);
  const [refillingPreviewImage, setRefillingPreviewImage] = useState<string | null>(null);
  const [filterDateFrom, setFilterDateFrom] = useState("");
  const [filterDateTo, setFilterDateTo] = useState("");
  const [filterState, setFilterState] = useState("");
  const [showFilters, setShowFilters] = useState(false);

  const authToken = useSelector((state: any) => state.loginData?.authToken);
  const id = useSelector((state: any) => state.loginData?.id);
  const userRole = useSelector((state: any) => state.loginData?.role);
  const userName = useSelector((state: any) => state.loginData?.name);
  const username = useSelector((state: any) => state.loginData?.username || state.loginData?.username_);

  // Get today's date in YYYY-MM-DD format
  const getTodayDate = (): string => {
    const today = new Date();
    return today.toISOString().split('T')[0];
  };

  // Calculate next visit date (3 months - 1 day from visit date)
  const calculateNextVisitDate = (visitDate: string): string => {
    if (!visitDate) return "";
    const date = new Date(visitDate);
    // Add 3 months
    date.setMonth(date.getMonth() + 3);
    // Subtract 1 day
    date.setDate(date.getDate() - 1);
    // Format as YYYY-MM-DD
    return date.toISOString().split('T')[0];
  };

  const todayDate = getTodayDate();
  const [formData, setFormData] = useState<AMCFormData>({
    state: "",
    locationCode: "",
    oldLocationCode: "",
    visitPeriod: "",
    visitDate: todayDate,
    reportNo: "",
    nextVisitDate: calculateNextVisitDate(todayDate),
    visitedBy: String(id || ""),
    needsRefilling: undefined,
    noVisitPeriod: "",
    dcDoc: "",
  });

  // Fetch all AMC records for search and filtering
  const fetchAllAMCForSearch = async () => {
    if (!authToken || !id) return;

    setLoading(true);
    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/amc?page=1&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setAllAMCRecords(response.data.amcRecords || []);
      setTotalAMC(response.data.totalCount || 0);
    } catch (error) {
      console.error("Error fetching all AMC records:", error);
    } finally {
      setLoading(false);
    }
  };

  // Get paginated records based on current page
  const getPaginatedRecords = (recordsToDisplay: AMC[]) => {
    const startIndex = (page - 1) * 10;
    const endIndex = startIndex + 10;
    return recordsToDisplay.slice(startIndex, endIndex);
  };

  const normalizeState = (state: string) =>
    (state || "").toLowerCase().replace(/\s+/g, "");

  const siteMatchesState = (siteState: string, selectedState: string) =>
    normalizeState(siteState) === normalizeState(selectedState);

  const getSitesForState = (state: string) =>
    sites.filter((site) => siteMatchesState(site.state, state));

  const buildUniqueOptions = (values: string[]) => {
    const seen = new Set<string>();
    return values
      .map((value) => value?.trim())
      .filter((value): value is string => {
        if (!value || seen.has(value)) return false;
        seen.add(value);
        return true;
      })
      .map((value) => ({ value, label: value }));
  };

  const newLocationCodeOptions = useMemo(
    () =>
      buildUniqueOptions(
        getSitesForState(formData.state).map((site) => site.locationCode)
      ),
    [sites, formData.state]
  );

  const oldLocationCodeOptions = useMemo(
    () =>
      buildUniqueOptions(
        getSitesForState(formData.state).map((site) => site.oldLocationCode)
      ),
    [sites, formData.state]
  );

  // Fetch all sites (paginated) for location code dropdowns
  const fetchSites = async () => {
    if (!authToken || !id) return;

    setLoadingSites(true);
    try {
      const pageSize = 500;
      const headers = { Authorization: `Bearer ${authToken}` };
      const firstResponse = await axios.get(
        `${API_BASE_URL}account/${id}/sites?page=1&limit=${pageSize}`,
        { headers }
      );
      const totalCount = firstResponse.data.totalCount || 0;
      let allSites: any[] = [...(firstResponse.data.sites || [])];
      let currentPage = 2;

      while (allSites.length < totalCount) {
        const response = await axios.get(
          `${API_BASE_URL}account/${id}/sites?page=${currentPage}&limit=${pageSize}`,
          { headers }
        );
        allSites = [...allSites, ...(response.data.sites || [])];
        currentPage++;
      }

      setSites(allSites);
    } catch (error) {
      console.error("Error fetching sites:", error);
    } finally {
      setLoadingSites(false);
    }
  };

  // Fetch all AMC records for visit period filtering (no pagination)
  const fetchAllAMCRecords = async (): Promise<AMC[]> => {
    if (!authToken || !id) return [];

    try {
      const response = await axios.get(
        `${API_BASE_URL}account/${id}/amc?page=1&limit=10000`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      return response.data.amcRecords || [];
    } catch (error) {
      console.error("Error fetching all AMC records:", error);
      return [];
    }
  };

  // Fetch used visit periods for a location
  const fetchUsedVisitPeriods = async (locationCode: string, state: string) => {
    if (!locationCode) {
      setUsedVisitPeriods([]);
      return;
    }

    try {
      // Fetch all AMC records to get accurate used visit periods
      const allAMCRecords = await fetchAllAMCRecords();
      
      const usedPeriods: string[] = [];
      allAMCRecords.forEach((amc) => {
        if (
          amc.locationCode.toLowerCase() === locationCode.toLowerCase() &&
          amc.state.toLowerCase() === state.toLowerCase()
        ) {
          usedPeriods.push(amc.visitPeriod);
        }
      });
      setUsedVisitPeriods(usedPeriods);
    } catch (error) {
      console.error("Error fetching used visit periods:", error);
      setUsedVisitPeriods([]);
    }
  };

  // Handle state change and auto-update location code options
  const handleStateChange = (value: string) => {
    setFormData({ ...formData, state: value, locationCode: "", oldLocationCode: "" });
    setUsedVisitPeriods([]);
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
  };

  // Handle location code change and fetch used visit periods
  const handleLocationCodeChange = (value: string) => {
    // Find the site to get oldLocationCode
    const selectedSite = sites.find(
      (site) => site.locationCode === value && siteMatchesState(site.state, formData.state)
    );
    const oldCode = selectedSite?.oldLocationCode || "";
    
    setFormData({ ...formData, locationCode: value, oldLocationCode: oldCode, visitPeriod: "" });
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
    fetchUsedVisitPeriods(value, formData.state);
  };

  // Handle old location code change and auto-select location code
  const handleOldLocationCodeChange = (value: string) => {
    // Find the site to get locationCode
    const selectedSite = sites.find(
      (site) => site.oldLocationCode === value && siteMatchesState(site.state, formData.state)
    );
    const locCode = selectedSite?.locationCode || "";
    
    setFormData({ ...formData, locationCode: locCode, oldLocationCode: value, visitPeriod: "" });
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
    if (locCode) {
      fetchUsedVisitPeriods(locCode, formData.state);
    }
  };

  // Handle visit date change and auto-calculate next visit date
  const handleVisitDateChange = (value: string) => {
    const nextDate = calculateNextVisitDate(value);
    setFormData({
      ...formData,
      visitDate: value,
      nextVisitDate: nextDate,
    });
  };

  useEffect(() => {
    if (authToken && id) {
      fetchAllAMCForSearch();
      fetchSites();
      // Ensure visitedBy is always set to current logged-in user ID as string
      setFormData((prevData) => ({
        ...prevData,
        visitedBy: String(id),
      }));
    }
  }, [authToken, id]);

  // Reset page to 1 when filters change
  useEffect(() => {
    setPage(1);
  }, [searchTerm, filterDateFrom, filterDateTo, filterState]);

  // Prevent background scroll while modals are open (fixes mobile keyboard/layout issues)
  useEffect(() => {
    if (!isDrawerOpen && !showRefillingForm) return;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, [isDrawerOpen, showRefillingForm]);

  // Validate form
  const validateForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!formData.state.trim()) newErrors.state = "State is required";
    if (!formData.locationCode.trim())
      newErrors.locationCode = "New location code is required";
    if (!formData.oldLocationCode.trim())
      newErrors.oldLocationCode = "Old location code is required";
    if (!formData.visitPeriod.trim())
      newErrors.visitPeriod = "Visit period is required";
    if (!formData.visitDate.trim())
      newErrors.visitDate = "Visit date is required";
    if (!formData.reportNo.trim())
      newErrors.reportNo = "Report number is required";
    if (!formData.nextVisitDate.trim())
      newErrors.nextVisitDate = "Next visit date is required";
    if (needsRefilling === null)
      newErrors.needsRefilling = "Please select refilling option";
    if (needsRefilling === false && !formData.noVisitPeriod?.trim())
      newErrors.noVisitPeriod = "No visit period is required when refilling is not needed";
    if (!formData.dcDoc || !formData.dcDoc.trim())
      newErrors.dcDoc = "Document/Image is required";
    // visitedBy is automatically set and cannot be empty

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle image upload
  const handleImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate that visitDate and visitPeriod are set before uploading
    if (!formData.visitDate || !formData.visitPeriod) {
      alert("Please select Visit Date and Visit Period before uploading an image.");
      event.target.value = "";
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setPreviewImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to Google Drive
    setUploadingImage(true);
    try {
      const uploadFormData = new FormData();
      // Rename file to location code
      const fileExtension = file.name.split('.').pop();
      const newFileName = `${formData.locationCode}.${fileExtension}`;
      const renamedFile = new File([file], newFileName, { type: file.type });

      uploadFormData.append("file", renamedFile);
      uploadFormData.append("visitDate", formData.visitDate);
      uploadFormData.append("visitPeriod", formData.visitPeriod);

      console.log("Uploading image to Google Drive...", newFileName, "folder:", formData.visitDate, formData.visitPeriod);

      const response = await axios.post(
        `${API_BASE_URL}account/${id}/amc/upload-image`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      console.log("Upload response:", response.data);

      if (response.data.driveLink) {
        console.log("Got drive link:", response.data.driveLink);
        setFormData({
          ...formData,
          dcDoc: response.data.driveLink,
        });
        alert(`Image uploaded successfully!\nLink: ${response.data.driveLink}`);
      } else {
        console.error("No driveLink in response:", response.data);
        alert("Image uploaded but no link received. Response: " + JSON.stringify(response.data));
      }
    } catch (error: any) {
      console.error("Error uploading image:", error);
      const errorMsg = error.response?.data?.message || error.message || "Failed to upload image";
      alert(`Error: ${errorMsg}`);
      setPreviewImage(null);
    } finally {
      setUploadingImage(false);
    }
  };

  // Create/Update AMC
  const handleCreateAMC = async () => {
    if (!validateForm()) return;

    // If refilling is needed, show refilling form instead
    if (needsRefilling === true && !showRefillingForm) {
      // Pre-fill refilling form with AMC data
      // oldLocationCode is already populated in formData when location code is selected
      setRefillingFormData({
        ...refillingFormData,
        state: formData.state,
        locationCode: formData.locationCode,
        oldLocationCode: formData.oldLocationCode,
        visitPeriod: formData.visitPeriod,
        visitDate: formData.visitDate,
        additional_field_1: "",
        additional_field_2: "",
      });
      setRefillingCreatedByName(userName && userName.trim() ? userName : "Current User");
      setRefillingCreatedById(String(id || ""));
      setShowRefillingForm(true);
      return;
    }

    setLoading(true);
    try {
      const payload: any = {
        state: formData.state,
        locationCode: formData.locationCode,
        oldLocationCode: formData.oldLocationCode,
        visitPeriod: formData.visitPeriod,
        visitDate: formData.visitDate,
        reportNo: formData.reportNo,
        nextVisitDate: formData.nextVisitDate,
        visitedBy: String(id), // Ensure it's the logged-in user ID as string
        needsRefilling: needsRefilling,
      };

      // Add noVisitPeriod if refilling is not needed
      if (needsRefilling === false && formData.noVisitPeriod) {
        payload.noVisitPeriod = formData.noVisitPeriod;
      }
      
      // Only include dcDoc if it has a value
      if (formData.dcDoc && formData.dcDoc.trim()) {
        payload.dcDoc = formData.dcDoc;
      } else {
        payload.dcDoc = "";
      }

      if (editingVisitId) {
        // Update
        await axios.put(
          `${API_BASE_URL}account/${id}/amc/${editingVisitId}/update`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("AMC record updated successfully");
      } else {
        // Create
        await axios.post(
          `${API_BASE_URL}account/${id}/amc/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("AMC record created successfully");
      }

      setTimeout(() => setSuccessMessage(""), 3000);
      closeDrawer();
      fetchAllAMCForSearch();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Error saving AMC record";
      console.error("Error:", errorMsg);
      if (error.response?.status === 409) {
        setErrors({
          locationCode: errorMsg,
          visitPeriod: errorMsg,
        });
      }
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Handle refilling form submission
  const handleRefillingSubmit = async () => {
    if (!validateRefillingForm()) return;

    setLoading(true);
    try {
      const payload: any = {
        state: refillingFormData.state,
        locationCode: refillingFormData.locationCode,
        oldLocationCode: refillingFormData.oldLocationCode,
        visitPeriod: refillingFormData.visitPeriod,
        visitDate: refillingFormData.visitDate,
        dcNo: refillingFormData.dcNo,
      };

      // Add quantity fields - convert to number and include all with values
      QUANTITY_FIELDS.forEach((field) => {
        const value = refillingFormData[field.key as keyof RefillingFormData];
        const numValue = value === "" || value === 0 ? 0 : parseInt(String(value)) || 0;
        payload[field.key] = numValue;
      });

      // Add text fields (additional information)
      TEXT_FIELDS.forEach((field) => {
        const value = refillingFormData[field.key as keyof RefillingFormData];
        payload[field.key] = String(value || "");
      });

      // Add document if it has a value
      if (refillingFormData.refillingDoc && refillingFormData.refillingDoc.trim()) {
        payload.refilling_doc = refillingFormData.refillingDoc;
      }

      await axios.post(
        `${API_BASE_URL}account/${id}/refilling/create`,
        payload,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );

      setSuccessMessage("Refilling record created successfully!");
      setTimeout(() => setSuccessMessage(""), 3000);
      
      // Close refilling form and proceed with AMC save
      setShowRefillingForm(false);
      closeRefilling();
      
      // Now save the AMC record
      await savAMCRecord();
    } catch (error: any) {
      console.error("Error creating refilling:", error);
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to save refilling";
      alert(`Error: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };

  // Save AMC record
  const savAMCRecord = async () => {
    setLoading(true);
    try {
      const payload: any = {
        state: formData.state,
        locationCode: formData.locationCode,
        oldLocationCode: formData.oldLocationCode,
        visitPeriod: formData.visitPeriod,
        visitDate: formData.visitDate,
        reportNo: formData.reportNo,
        nextVisitDate: formData.nextVisitDate,
        visitedBy: String(id),
        needsRefilling: needsRefilling,
      };

      // Add noVisitPeriod if refilling is not needed
      if (needsRefilling === false && formData.noVisitPeriod) {
        payload.noVisitPeriod = formData.noVisitPeriod;
      }
      
      if (formData.dcDoc && formData.dcDoc.trim()) {
        payload.dcDoc = formData.dcDoc;
      } else {
        payload.dcDoc = "";
      }

      if (editingVisitId) {
        await axios.put(
          `${API_BASE_URL}account/${id}/amc/${editingVisitId}/update`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("AMC record updated successfully");
      } else {
        await axios.post(
          `${API_BASE_URL}account/${id}/amc/create`,
          payload,
          {
            headers: {
              Authorization: `Bearer ${authToken}`,
            },
          }
        );
        setSuccessMessage("AMC record created successfully");
      }

      setTimeout(() => setSuccessMessage(""), 3000);
      closeDrawer();
      fetchAllAMCForSearch();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Error saving AMC record";
      console.error("Error:", errorMsg);
      if (error.response?.status === 409) {
        setErrors({
          locationCode: errorMsg,
          visitPeriod: errorMsg,
        });
      }
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Validate refilling form
  const validateRefillingForm = (): boolean => {
    const newErrors: { [key: string]: string } = {};

    if (!refillingFormData.visitPeriod.trim()) newErrors.visitPeriod = "Visit period is required";
    if (!refillingFormData.dcNo.trim()) newErrors.dcNo = "DC No is required";
    if (!refillingFormData.refillingDoc || !refillingFormData.refillingDoc.trim())
      newErrors.refillingDoc = "Delivery Challan (DC) Image is required";

    setRefillingErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle refilling image upload
  const handleRefillingImageUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Backend requires visitDate for refilling image folder routing
    if (!refillingFormData.visitDate) {
      alert("Visit Date is required before uploading refilling image.");
      event.target.value = "";
      return;
    }

    // Show preview
    const reader = new FileReader();
    reader.onloadend = () => {
      setRefillingPreviewImage(reader.result as string);
    };
    reader.readAsDataURL(file);

    // Upload to Google Drive
    setRefillingUploadingImage(true);
    try {
      // Rename file to location code
      const fileExtension = file.name.split('.').pop();
      const newFileName = `${refillingFormData.locationCode}.${fileExtension}`;
      const renamedFile = new File([file], newFileName, { type: file.type });

      const uploadFormData = new FormData();
      uploadFormData.append("file", renamedFile);
      uploadFormData.append("visitDate", refillingFormData.visitDate);
      uploadFormData.append("visitPeriod", refillingFormData.visitPeriod || "");

      const response = await axios.post(
        `${API_BASE_URL}account/${id}/refilling/upload-image`,
        uploadFormData,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      if (response.data.driveLink) {
        setRefillingFormData({
          ...refillingFormData,
          refillingDoc: response.data.driveLink,
        });
        alert(`Image uploaded successfully!\nLink: ${response.data.driveLink}`);
      } else {
        alert("Image uploaded but no link received.");
      }
    } catch (error: any) {
      console.error("Error uploading image:", error);
      const errorMsg =
        error.response?.data?.message || error.message || "Failed to upload image";
      alert(`Error: ${errorMsg}`);
      setRefillingPreviewImage(null);
    } finally {
      setRefillingUploadingImage(false);
    }
  };

  // Close refilling form
  const closeRefilling = () => {
    setShowRefillingForm(false);
    setRefillingFormData({
      state: "",
      locationCode: "",
      oldLocationCode: "",
      visitPeriod: "",
      visitDate: "",
      dcNo: "",
      abc_2_0_kg: 0,
      abc_4_0_kg: 0,
      abc_6_0_kg: 0,
      abc_9_0_kg: 0,
      co2_2_0_kg: 0,
      co2_4_5_kg: 0,
      co2_9_0_kg: 0,
      foam_9_0_lrs: 0,
      clean_agent_4_0_kg: 0,
      clean_agent_10_0_kg: 0,
      safety_pin: 0,
      pressure_gauge: 0,
      abc_hose: 0,
      co2_hose: 0,
      co2_valve: 0,
      abc_valve: 0,
      additional_field_1: "",
      additional_field_2: "",
      refillingDoc: "",
    });
    setRefillingPreviewImage(null);
    setRefillingErrors({});
    setRefillingCreatedByName("");
    setRefillingCreatedById("");
  };

  // Edit AMC
  const handleEditAMC = (amc: AMC) => {
    setEditingVisitId(amc.visitId);
    setFormData({
      state: amc.state,
      locationCode: amc.locationCode,
      oldLocationCode: amc.oldLocationCode,
      visitPeriod: amc.visitPeriod,
      visitDate: amc.visitDate,
      reportNo: amc.reportNo,
      nextVisitDate: amc.nextVisitDate,
      visitedBy: amc.visitedBy,
      needsRefilling: amc.needsRefilling,
      noVisitPeriod: amc.noVisitPeriod || "",
      dcDoc: amc.dcDoc || "",
    });
    setNeedsRefilling(amc.needsRefilling ?? null);
    if (amc.dcDoc) {
      setPreviewImage(amc.dcDoc);
    }
    // Fetch used periods (excluding current record)
    const usedPeriods = amcRecords
      .filter(
        (record) =>
          record.locationCode.toLowerCase() === amc.locationCode.toLowerCase() &&
          record.state.toLowerCase() === amc.state.toLowerCase() &&
          record.visitId !== amc.visitId
      )
      .map((record) => record.visitPeriod);
    setUsedVisitPeriods(usedPeriods);
    setIsDrawerOpen(true);
  };

  // Delete AMC
  const handleDeleteAMC = async () => {
    if (!deleteConfirm) return;

    setLoading(true);
    try {
      await axios.delete(
        `${API_BASE_URL}account/${id}/amc/${deleteConfirm.visitId}/delete`,
        {
          headers: {
            Authorization: `Bearer ${authToken}`,
          },
        }
      );
      setSuccessMessage("AMC record deleted successfully");
      setTimeout(() => setSuccessMessage(""), 3000);
      setDeleteConfirm(null);
      fetchAllAMCForSearch();
    } catch (error: any) {
      const errorMsg =
        error.response?.data?.message || "Error deleting AMC record";
      console.error("Error:", errorMsg);
      alert(errorMsg);
    } finally {
      setLoading(false);
    }
  };

  // Close drawer and reset form
  const closeDrawer = () => {
    setIsDrawerOpen(false);
    setEditingVisitId(null);
    setFormData({
      state: "",
      locationCode: "",
      oldLocationCode: "",
      visitPeriod: "",
      visitDate: getTodayDate(),
      reportNo: "",
      nextVisitDate: "",
      visitedBy: String(id || ""),
      needsRefilling: undefined,
      noVisitPeriod: "",
      dcDoc: "",
    });
    setPreviewImage(null);
    setErrors({});
    setUsedVisitPeriods([]);
    setLocationCodeSelectOpen(false);
    setOldLocationCodeSelectOpen(false);
    setNeedsRefilling(null);
    setShowRefillingForm(false);
    closeRefilling();
  };

  // Filter and sort AMC records by creation date (newest first) - search across all records
  const filteredAMC = getPaginatedRecords(
    allAMCRecords
      .filter((amc) => {
        // Search filter
        const searchMatch =
          amc.reportNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
          amc.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
          amc.locationCode.toLowerCase().includes(searchTerm.toLowerCase());

        // Date filter - safely handle invalid dates
        let visitDate = "";
        try {
          const date = new Date(amc.visitDate);
          if (!isNaN(date.getTime())) {
            visitDate = date.toISOString().split('T')[0];
          }
        } catch (e) {
          visitDate = "";
        }
        const dateFromMatch = !filterDateFrom || !visitDate || visitDate >= filterDateFrom;
        const dateToMatch = !filterDateTo || !visitDate || visitDate <= filterDateTo;

        // State filter
        const stateMatch = !filterState || amc.state.toLowerCase() === filterState.toLowerCase();

        // No search: own records only. Search active: all matching records (own + others).
        const userMatch = userRole === "admin" || searchTerm !== "" || String(amc.createdBy) === String(id);

        return searchMatch && dateFromMatch && dateToMatch && stateMatch && userMatch;
      })
      .sort((a, b) => {
        try {
          const timeA = new Date(b.createdAt).getTime();
          const timeB = new Date(a.createdAt).getTime();
          return timeA - timeB;
        } catch (e) {
          return 0;
        }
      })
  );

  // Get total filtered count for pagination
  const filteredTotalCount = allAMCRecords.filter((amc) => {
    // Search filter
    const searchMatch =
      amc.reportNo.toLowerCase().includes(searchTerm.toLowerCase()) ||
      amc.state.toLowerCase().includes(searchTerm.toLowerCase()) ||
      amc.locationCode.toLowerCase().includes(searchTerm.toLowerCase());

    // Date filter - safely handle invalid dates
    let visitDate = "";
    try {
      const date = new Date(amc.visitDate);
      if (!isNaN(date.getTime())) {
        visitDate = date.toISOString().split('T')[0];
      }
    } catch (e) {
      visitDate = "";
    }
    const dateFromMatch = !filterDateFrom || !visitDate || visitDate >= filterDateFrom;
    const dateToMatch = !filterDateTo || !visitDate || visitDate <= filterDateTo;

    // State filter
    const stateMatch = !filterState || amc.state.toLowerCase() === filterState.toLowerCase();

    // No search: own records only. Search active: all matching records (own + others).
    const userMatch = userRole === "admin" || searchTerm !== "" || String(amc.createdBy) === String(id);

    return searchMatch && dateFromMatch && dateToMatch && stateMatch && userMatch;
  }).length;

  return (
    <div className="w-full max-w-7xl mx-auto p-4 md:p-6">
      {/* Header */}
      <div className="mb-6 flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">AMC Management</h1>
          <p className="text-gray-600 mt-1">
            Manage Annual Maintenance Contract visits
          </p>
        </div>
        <Button
          onClick={() => setIsDrawerOpen(true)}
          className="bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white gap-2"
        >
          <Plus size={20} />
          Add New AMC Visit
        </Button>
      </div>

      {/* Success Message */}
      {successMessage && (
        <div className="mb-4 p-4 bg-green-100 text-green-800 rounded-lg flex items-center gap-2">
          <AlertCircle size={20} />
          {successMessage}
        </div>
      )}

      {/* Search Bar */}
      <div className="mb-6 relative">
        <Search className="absolute left-3 top-3 text-gray-400" size={20} />
        <Input
          type="text"
          placeholder="Search by report number, state, or location code..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 py-2 border border-gray-300 rounded-lg w-full"
        />
      </div>

      {/* Mobile Filter Toggle Button */}
      <div className="mb-4 md:hidden">
        <Button
          onClick={() => setShowFilters(!showFilters)}
          variant="outline"
          className="w-full flex items-center justify-between"
        >
          <span>🔧 Filters</span>
          <span>{showFilters ? "−" : "+"}</span>
        </Button>
      </div>

      {/* Filter Section - Hidden on mobile unless toggled */}
      {(showFilters || window.innerWidth >= 768) && (
        <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            {/* Date From Filter */}
            <div>
              <Label className="text-gray-700 font-semibold text-sm mb-2 block">
                Date From
              </Label>
              <Input
                type="date"
                value={filterDateFrom}
                onChange={(e) => setFilterDateFrom(e.target.value)}
                className="w-full py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* Date To Filter */}
            <div>
              <Label className="text-gray-700 font-semibold text-sm mb-2 block">
                Date To
              </Label>
              <Input
                type="date"
                value={filterDateTo}
                onChange={(e) => setFilterDateTo(e.target.value)}
                className="w-full py-2 border border-gray-300 rounded-lg"
              />
            </div>

            {/* State Filter */}
            <div>
              <Label className="text-gray-700 font-semibold text-sm mb-2 block">
                Filter by State
              </Label>
              <Select value={filterState} onValueChange={setFilterState}>
                <SelectTrigger>
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent className="bg-white">
                  {STATE_OPTIONS.map((option) => (
                    <SelectItem key={option.value} value={option.value}>
                      {option.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Clear Filters Button */}
            <div className="flex items-end">
              <Button
                onClick={() => {
                  setFilterDateFrom("");
                  setFilterDateTo("");
                  setFilterState("");
                }}
                variant="outline"
                className="w-full"
              >
                Clear Filters
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Loading State */}
      {loading && (
        <div className="flex justify-center items-center py-12">
          <Loader2 className="animate-spin text-cyan-500" size={40} />
        </div>
      )}

      {/* AMC Records Grid */}
      {!loading && (
        <div className="space-y-4">
          {filteredAMC.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Calendar size={48} className="mx-auto text-gray-400 mb-4" />
              <p className="text-gray-600 text-lg">No AMC records found</p>
            </div>
          ) : (
            filteredAMC.map((amc) => (
              <div
                key={amc.visitId}
                className="bg-white rounded-lg border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow"
              >
                <div className="p-4 md:p-6 flex flex-col md:flex-row items-start md:items-center gap-4">
                  {/* Left Section - Report & Dates */}
                  <div className="flex-1 min-w-0">
                    <h3 className="text-lg font-bold text-gray-800 truncate">
                      #{amc.locationCode}
                    </h3>
                    <div className="mt-2 space-y-1 text-sm text-gray-600">
                      <p>
                        <span className="font-semibold">Visit Date:</span>{" "}
                        {new Date(amc.visitDate).toLocaleDateString()}
                      </p>
                     
                    </div>
                  </div>

                  {/* Middle Section - Location Details */}
                  <div className="flex-1 min-w-0">
                    <div className="space-y-2 text-sm">
                       {amc.dcDoc && (
                      <div className="w-full md:w-auto">
                        <button
                          onClick={() => {
                            setImageModalUrl(amc.dcDoc || null);
                            setImageModalOpen(true);
                          }}
                          className="text-cyan-600 hover:text-cyan-800 text-sm font-semibold flex items-center gap-1 cursor-pointer"
                        >
                          <ImageIcon size={16} />
                          View Document
                        </button>
                      </div>
                    )}
                         <div className="w-full md:w-auto ">
                    
                      <p className="text-xs text-gray-500">
                        <span className="font-semibold">Created By:</span>{" "}
                        {amc.createdByName || "Unknown"}
                      </p>
                    </div>
                    
                       
                    </div>
                  </div>

                  {/* Right Section - Document & Actions */}
                  <div className="flex flex-col items-end gap-2 w-full md:w-auto">
                    {/* Created By Info */}
                 
                 
                    <div className="flex gap-2 w-full md:w-auto">
                      {/* Edit button - only for admin */}
                      {userRole === "admin" && (
                        <Button
                          onClick={() => handleEditAMC(amc)}
                          size="sm"
                          className="bg-blue-500 hover:bg-blue-600 text-white gap-1 flex-1 md:flex-none"
                        >
                          <Edit2 size={16} />
                          View/Edit
                        </Button>
                      )}
                      
                      {/* Delete button - for admin or staff (owner) */}
                      {userRole === "admin" || String(amc.createdBy) === String(id) ? (
                        <Button
                          onClick={() =>
                            setDeleteConfirm({
                              visitId: amc.visitId,
                              reportNo: amc.reportNo,
                            })
                          }
                          size="sm"
                          className="bg-red-500 hover:bg-red-600 text-white gap-1 flex-1 md:flex-none"
                        >
                          <Trash2 size={16} />
                          Delete
                        </Button>
                      ) : null}
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      )}

      {/* Pagination */}
      {filteredTotalCount > 0 && (
        <div className="mt-6 flex justify-between items-center">
          <span className="text-sm text-gray-600">
            Showing {Math.max(1, (page - 1) * 10 + 1)} to {Math.min(page * 10, filteredTotalCount)} of {filteredTotalCount} AMC
            records
          </span>
          <div className="flex gap-2">
            <Button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              variant="outline"
            >
              Previous
            </Button>
            <Button
              onClick={() => setPage(page + 1)}
              disabled={page * 10 >= filteredTotalCount}
              variant="outline"
            >
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Drawer/Modal for Create/Edit */}
      {isDrawerOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div
            data-amc-drawer
            className="bg-white w-full md:w-96 max-h-[90vh] rounded-lg shadow-lg flex flex-col"
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Drawer Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white p-6 flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold">
                {editingVisitId ? "Edit AMC Visit" : "Create New AMC Visit"}
              </h2>
              <button
                onClick={closeDrawer}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1"
              >
                <X size={24} />
              </button>
            </div>

            {/* Drawer Content - Scrollable */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* State */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  State *
                </Label>
                <Select
                  value={formData.state}
                  onValueChange={handleStateChange}
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select state" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {STATE_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {errors.state && (
                  <p className="text-red-500 text-sm mt-1">{errors.state}</p>
                )}
              </div>

              {/* Location Code and Old Location Code - Side by side with search */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Location Code */}
                <div>
                  <Label className="text-gray-700 font-semibold mb-2 block">
                    New Location Code *
                  </Label>
                  <SearchableSelect
                    value={formData.locationCode}
                    onValueChange={handleLocationCodeChange}
                    options={newLocationCodeOptions}
                    open={locationCodeSelectOpen}
                    onOpenChange={setLocationCodeSelectOpen}
                    disabled={!formData.state || loadingSites}
                    placeholder={loadingSites ? "Loading..." : "Select"}
                    searchPlaceholder="Search new location code..."
                    emptyMessage="No location codes available"
                    noResultsMessage="No options found"
                  />
                  {!formData.state && (
                    <p className="text-gray-500 text-sm mt-1">Please select a state first</p>
                  )}
                  {errors.locationCode && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.locationCode}
                    </p>
                  )}
                </div>

                {/* Old Location Code */}
                <div>
                  <Label className="text-gray-700 font-semibold mb-2 block">
                    Old Location Code *
                  </Label>
                  <SearchableSelect
                    value={formData.oldLocationCode}
                    onValueChange={handleOldLocationCodeChange}
                    options={oldLocationCodeOptions}
                    open={oldLocationCodeSelectOpen}
                    onOpenChange={setOldLocationCodeSelectOpen}
                    disabled={!formData.state || loadingSites}
                    placeholder={loadingSites ? "Loading..." : "Select"}
                    searchPlaceholder="Search old location code..."
                    emptyMessage="No location codes available"
                    noResultsMessage="No options found"
                  />
                  {!formData.state && (
                    <p className="text-gray-500 text-sm mt-1">Please select a state first</p>
                  )}
                  {errors.oldLocationCode && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.oldLocationCode}
                    </p>
                  )}
                </div>
              </div>

              {/* Visit Period */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Visit Period *
                </Label>
                <Select
                  value={formData.visitPeriod}
                  onValueChange={(value) =>
                    setFormData({ ...formData, visitPeriod: value })
                  }
                  disabled={!formData.locationCode}
                >
                  <SelectTrigger>
                    <SelectValue placeholder={!formData.locationCode ? "Select location first" : "Select period"} />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {VISIT_PERIOD_OPTIONS.filter(
                      (option) => !usedVisitPeriods.includes(option.value)
                    ).map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {!formData.locationCode && (
                  <p className="text-gray-500 text-sm mt-1">Please select a location code first</p>
                )}
                {errors.visitPeriod && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.visitPeriod}
                  </p>
                )}
              </div>

              {/* Visit Date */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Visit Date *
                </Label>
                <Input
                  type="date"
                  value={formData.visitDate}
                  onChange={(e) => handleVisitDateChange(e.target.value)}
                  className="w-full"
                />
                {errors.visitDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.visitDate}
                  </p>
                )}
              </div>

              {/* Report Number */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Report Number *
                </Label>
                <Input
                  type="text"
                  value={formData.reportNo}
                  onChange={(e) =>
                    setFormData({ ...formData, reportNo: e.target.value })
                  }
                  placeholder="Enter report number"
                  className="w-full"
                />
                {errors.reportNo && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.reportNo}
                  </p>
                )}
              </div>

              {/* Next Visit Date */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Next Visit Date *
                </Label>
                <Input
                  type="date"
                  value={formData.nextVisitDate}
                  onChange={(e) =>
                    setFormData({ ...formData, nextVisitDate: e.target.value })
                  }
                  className="w-full"
                />
                {errors.nextVisitDate && (
                  <p className="text-red-500 text-sm mt-1">
                    {errors.nextVisitDate}
                  </p>
                )}
              </div>

              {/* Visited By - Display logged-in user (non-editable) */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Visited By *
                </Label>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-gray-700 font-semibold">
                  {username && username.trim() ? username : userName && userName.trim() ? userName : "Current User"}
                </div>
                <p className="text-gray-500 text-xs mt-1">Automatically set to your logged-in user</p>
              </div>

              {/* Refilling Option */}
              <div>
                <Label className="text-gray-700 font-semibold mb-3 block">
                  Refilling? *
                </Label>
                <div className="flex gap-3">
                  <Button
                    onClick={() => setNeedsRefilling(true)}
                    className={`flex-1 ${
                      needsRefilling === true
                        ? "bg-green-600 hover:bg-green-700 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    }`}
                  >
                    Yes
                  </Button>
                  <Button
                    onClick={() => setNeedsRefilling(false)}
                    className={`flex-1 ${
                      needsRefilling === false
                        ? "bg-red-600 hover:bg-red-700 text-white"
                        : "bg-gray-200 hover:bg-gray-300 text-gray-800"
                    }`}
                  >
                    No
                  </Button>
                </div>
                {needsRefilling === null && (
                  <p className="text-red-500 text-sm mt-1">Please select an option</p>
                )}
              </div>

              {/* No Visit Period - Only show when refilling is "No" */}
              {needsRefilling === false && (
                <div>
                  <Label className="text-gray-700 font-semibold mb-2 block">
                    No Visit Period *
                  </Label>
                  <Select
                    value={formData.noVisitPeriod || ""}
                    onValueChange={(value) =>
                      setFormData({ ...formData, noVisitPeriod: value })
                    }
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select period" />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {VISIT_PERIOD_OPTIONS.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                  {errors.noVisitPeriod && (
                    <p className="text-red-500 text-sm mt-1">
                      {errors.noVisitPeriod}
                    </p>
                  )}
                </div>
              )}

              {/* Image Upload */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Document/Image *
                </Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {previewImage ? (
                    <div className="space-y-3">
                      <img
                        src={previewImage}
                        alt="Preview"
                        className="w-full h-40 object-cover rounded"
                      />
                      <Button
                        onClick={() => {
                          document.getElementById("image-input")?.click();
                        }}
                        size="sm"
                        className="w-full"
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 className="animate-spin" size={16} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={16} />
                            Change Image
                          </>
                        )}
                      </Button>
                    </div>
                  ) : (
                    <div className="space-y-2">
                      <ImageIcon
                        className="mx-auto text-gray-400"
                        size={32}
                      />
                      <p className="text-gray-600 text-sm">
                        Click to upload or drag image
                      </p>
                      <Button
                        onClick={() => {
                          document.getElementById("image-input")?.click();
                        }}
                        size="sm"
                        disabled={uploadingImage}
                      >
                        {uploadingImage ? (
                          <>
                            <Loader2 className="animate-spin" size={16} />
                            Uploading...
                          </>
                        ) : (
                          <>
                            <Upload size={16} />
                            Select Image
                          </>
                        )}
                      </Button>
                    </div>
                  )}
                  <input
                    id="image-input"
                    type="file"
                    accept="image/*"
                    onChange={handleImageUpload}
                    className="hidden"
                  />
                </div>
                <p className="text-red-500 text-sm mt-1">Upload Only AMC Report</p>
                {errors.dcDoc && (
                  <p className="text-red-500 text-sm mt-1">{errors.dcDoc}</p>
                )}
              </div>
            </div>

            {/* Action Buttons - Fixed at Bottom */}
            <div className="flex gap-2 p-6 border-t border-gray-200 bg-white flex-shrink-0">
              <Button
                onClick={handleCreateAMC}
                disabled={loading || uploadingImage}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 className="animate-spin" size={16} />
                    Saving...
                  </>
                ) : editingVisitId ? (
                  "Update"
                ) : (
                  "Save"
                )}
              </Button>
              <Button
                onClick={closeDrawer}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Refilling Form Modal */}
      {showRefillingForm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-[60] flex items-center justify-center p-4">
          <div
            data-amc-refilling-modal
            className="bg-white w-full md:w-96 max-h-[90vh] rounded-lg shadow-lg flex flex-col"
            onPointerDown={(e) => e.stopPropagation()}
            onTouchStart={(e) => e.stopPropagation()}
          >
            {/* Refilling Form Header */}
            <div className="bg-gradient-to-r from-cyan-500 to-teal-500 text-white p-6 flex justify-between items-center flex-shrink-0">
              <h2 className="text-xl font-bold">Add Refilling Details</h2>
              <button
                onClick={() => {
                  setShowRefillingForm(false);
                  closeRefilling();
                }}
                className="text-white hover:bg-white hover:bg-opacity-20 rounded-lg p-1"
              >
                <X size={24} />
              </button>
            </div>

            {/* Refilling Form Content - Scrollable */}
            <div className="p-6 space-y-4 overflow-y-auto flex-1">
              {/* State (Read-only from AMC) */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">State</Label>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-gray-700 font-semibold">
                  {getStateLabel(refillingFormData.state)}
                </div>
              </div>

              {/* Location Code (Read-only from AMC) */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">New Location Code</Label>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-gray-700 font-semibold">
                  {refillingFormData.locationCode || "—"}
                </div>
              </div>

              {/* Old Location Code (Read-only from AMC) */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">Old Location Code</Label>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-gray-700 font-semibold">
                  {refillingFormData.oldLocationCode || "—"}
                </div>
              </div>

              {/* Visit Date (Read-only from AMC) */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">Visit Date</Label>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-gray-700 font-semibold">
                  {new Date(refillingFormData.visitDate).toLocaleDateString()}
                </div>
              </div>

              {/* Visit Period */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">Visit Period *</Label>
                <Select
                  value={refillingFormData.visitPeriod}
                  onValueChange={(value) =>
                    setRefillingFormData({ ...refillingFormData, visitPeriod: value })
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select period" />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {VISIT_PERIOD_OPTIONS.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                {refillingErrors.visitPeriod && (
                  <p className="text-red-500 text-sm mt-1">{refillingErrors.visitPeriod}</p>
                )}
              </div>

              {/* DC No */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">DC No *</Label>
                <Input
                  type="text"
                  value={refillingFormData.dcNo}
                  onChange={(e) =>
                    setRefillingFormData({ ...refillingFormData, dcNo: e.target.value })
                  }
                  placeholder="Enter DC number"
                  className="w-full"
                />
                {refillingErrors.dcNo && (
                  <p className="text-red-500 text-sm mt-1">{refillingErrors.dcNo}</p>
                )}
              </div>

              {/* Created By - Display logged-in user (non-editable) */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Created By *
                </Label>
                <div className="bg-gray-100 border border-gray-300 rounded-lg p-3 text-gray-700 font-semibold">
                  {refillingCreatedByName || "Current User"}
                </div>
                <p className="text-gray-500 text-xs mt-1">Automatically set to the user who created this record</p>
              </div>

              {/* Quantity Fields in 2-column layout */}
              <div className="bg-blue-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-3">Quantities</h3>
                <div className="grid grid-cols-2 gap-3">
                  {QUANTITY_FIELDS.map((field) => (
                    <div key={field.key}>
                      <Label className="text-gray-700 text-sm mb-1 block">
                        {field.label}
                      </Label>
                      <Input
                        type="number"
                        value={
                          refillingFormData[field.key as keyof RefillingFormData] === 0
                            ? ""
                            : refillingFormData[field.key as keyof RefillingFormData]
                        }
                        onChange={(e) =>
                          setRefillingFormData({
                            ...refillingFormData,
                            [field.key]: parseInt(e.target.value) || 0,
                          })
                        }
                        placeholder="Enter quantity"
                        className="w-full"
                        min="0"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Text Fields - Additional Information */}
              <div className="bg-purple-50 p-4 rounded-lg">
                <h3 className="font-bold text-gray-800 mb-3">Additional Information</h3>
                <div className="space-y-3">
                  {TEXT_FIELDS.map((field) => (
                    <div key={field.key}>
                      <Label className="text-gray-700 text-sm mb-1 block">
                        {field.label}
                      </Label>
                      <Input
                        type="text"
                        value={
                          refillingFormData[field.key as keyof RefillingFormData] || ""
                        }
                        onChange={(e) =>
                          setRefillingFormData({
                            ...refillingFormData,
                            [field.key]: e.target.value,
                          })
                        }
                        placeholder={`Enter ${field.label.toLowerCase()}`}
                        className="w-full"
                      />
                    </div>
                  ))}
                </div>
              </div>

              {/* Image Upload */}
              <div>
                <Label className="text-gray-700 font-semibold mb-2 block">
                  Delivery Challan (DC) Image *
                </Label>
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-4 text-center">
                  {refillingPreviewImage ? (
                    <div className="space-y-3">
                      <img
                        src={refillingPreviewImage}
                        alt="Preview"
                        className="max-h-40 mx-auto rounded-lg"
                      />
                      <label className="block">
                        <input
                          type="file"
                          accept="image/*"
                          onChange={handleRefillingImageUpload}
                          disabled={refillingUploadingImage}
                          className="hidden"
                        />
                        <span className="text-blue-500 hover:underline cursor-pointer text-sm">
                          {refillingUploadingImage ? "Uploading..." : "Change Image"}
                        </span>
                      </label>
                    </div>
                  ) : (
                    <label className="cursor-pointer">
                      <input
                        type="file"
                        accept="image/*"
                        onChange={handleRefillingImageUpload}
                        disabled={refillingUploadingImage}
                        className="hidden"
                      />
                      <div className="space-y-2">
                        <Upload
                          className="mx-auto text-gray-400"
                          size={32}
                        />
                        <p className="text-gray-600 text-sm">
                          {refillingUploadingImage
                            ? "Uploading..."
                            : "Click to upload or drag and drop"}
                        </p>
                        <p className="text-gray-400 text-xs">
                          PNG, JPG, GIF up to 10MB
                        </p>
                      </div>
                    </label>
                  )}
                </div>
                {refillingErrors.refillingDoc && (
                  <p className="text-red-500 text-sm mt-1">{refillingErrors.refillingDoc}</p>
                )}
              </div>
            </div>

            {/* Action Buttons - Fixed at Bottom */}
            <div className="flex gap-3 p-6 border-t border-gray-200 bg-white flex-shrink-0">
              <Button
                onClick={() => {
                  setShowRefillingForm(false);
                  closeRefilling();
                }}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
              <Button
                onClick={handleRefillingSubmit}
                disabled={loading}
                className="flex-1 bg-gradient-to-r from-cyan-500 to-teal-500 hover:from-cyan-600 hover:to-teal-600 text-white"
              >
                {loading ? (
                  <>
                    <Loader2 size={18} className="animate-spin mr-2" />
                    Saving...
                  </>
                ) : (
                  "Save Refilling & Continue"
                )}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Dialog */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-lg p-6 max-w-sm w-full">
            <div className="flex items-center gap-3 mb-4">
              <AlertCircle className="text-red-500" size={24} />
              <h3 className="text-lg font-bold text-gray-800">
                Confirm Deletion
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete AMC visit Report #{deleteConfirm.reportNo}? This action cannot be undone.
            </p>
            <div className="flex gap-2">
              <Button
                onClick={handleDeleteAMC}
                disabled={loading}
                className="flex-1 bg-red-500 hover:bg-red-600 text-white"
              >
                {loading ? "Deleting..." : "Delete"}
              </Button>
              <Button
                onClick={() => setDeleteConfirm(null)}
                variant="outline"
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* Image Modal */}
      {imageModalOpen && imageModalUrl && (
        <div className="fixed inset-0 bg-black bg-opacity-70 z-[100] flex items-center justify-center p-4">
          <div className="relative bg-white rounded-lg shadow-2xl max-w-2xl w-full">
            {/* Close Button */}
            <button
              onClick={() => {
                setImageModalOpen(false);
                setImageModalUrl(null);
              }}
              className="absolute top-4 right-4 bg-red-500 hover:bg-red-600 text-white rounded-full p-2 z-10"
            >
              <X size={24} />
            </button>

            {/* Image Container */}
            <div className="p-4 flex items-center justify-center bg-gray-100 rounded-lg">
              <img
                src={imageModalUrl}
                alt="Document"
                className="max-w-full max-h-[70vh] object-contain rounded"
              />
            </div>

            {/* Action Buttons */}
            <div className="p-4 border-t border-gray-200 flex gap-2">
              <a
                href={imageModalUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 bg-blue-500 hover:bg-blue-600 text-white font-semibold py-2 px-4 rounded text-center"
              >
                Open in New Tab
              </a>
              <button
                onClick={() => {
                  setImageModalOpen(false);
                  setImageModalUrl(null);
                }}
                className="flex-1 bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AMCPage;
